import type {
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
} from "@tanstack/react-query";

import type { ChargeDetail } from "@/types/charge";
import { ItemType } from "@/types/enums/item-type";

import { invalidateDoseInventory } from "./invalidate-dose-inventory";
import { chargeQueryKeys } from "@/api/provider/billing/charge-query";
import { patientDiscountsQueryKey } from "@/api/provider/billing/discount-query";
import { todaysItemsQueryKeys } from "@/api/provider/billing/todays-items-query";
import { membershipQueryKeys } from "@/api/provider/inventory/membership-query";
import { patientSoldPackageQueryKeys } from "@/api/provider/inventory/package-query";
import { loyaltyPointQueryKeys } from "@/api/provider/user/patient/loyalty-points-query";
import { patientQueryKeys } from "@/api/provider/user/patient/patient-query";
import { eventQueryKeys } from "@/query/check-in/calendar/event-query";
import { memberQueryKeys } from "@/query/inventory/member";
import { patientTimelineQueryKeys } from "@/query/user/patient/timeline";

export interface CompletedCheckoutInvalidation {
  patientId: string;
  charges: ChargeDetail[];
}

function startsWith(queryKey: QueryKey, prefix: readonly unknown[]): boolean {
  if (queryKey.length < prefix.length) return false;
  return prefix.every((part, index) => queryKey[index] === part);
}

function markQueriesStale(
  queryClient: QueryClient,
  filters: InvalidateQueryFilters,
) {
  // Checkout writes are read-after-write sensitive and some affected patient
  // and calendar reads are served by replicas. Mark all related queries stale
  // without immediately refetching so those replica-backed reads cannot replace
  // the cache with pre-checkout data. The next explicit fetch or mount retrieves
  // the completed checkout state; primary-backed balance mutations may also
  // refresh their narrower keys immediately at the mutation site.
  return queryClient.invalidateQueries({ ...filters, refetchType: "none" });
}

/**
 * Marks every server-derived cache changed by a completed checkout as stale.
 *
 * This intentionally runs at checkout completion rather than per payment. That
 * keeps split payments from repeatedly touching the cache and also covers
 * completion paths without a payment mutation, such as zero-dollar charges and
 * invoices.
 */
export async function invalidateCompletedCheckout(
  queryClient: QueryClient,
  { patientId, charges }: CompletedCheckoutInvalidation,
): Promise<void> {
  const items = charges.flatMap((charge) => charge.items ?? []);

  const affectsPackages = items.some(
    (item) =>
      item.item?.type === ItemType.PACKAGE ||
      !!item.soldPackageId ||
      !!item.packageId ||
      item.discounts?.some((use) => !!use.discount?.soldPackageId),
  );
  const affectsMemberships = items.some(
    (item) =>
      item.item?.type === ItemType.MEMBERSHIP ||
      item.discounts?.some((use) => !!use.discount?.memberId),
  );
  const affectsBankedItems = items.some(
    (item) => !!item.bankedItemId || (item.savedQuantity ?? 0) > 0,
  );
  const affectsInventory = items.some(
    (item) =>
      (item.shipmentIds?.length ?? 0) > 0 ||
      (item.shipmentAllocations?.length ?? 0) > 0 ||
      (item.item?.stocks?.length ?? 0) > 0 ||
      !!item.doseComponentIds?.length ||
      !!item.doseId,
  );
  const eventIds = new Set(
    charges
      .map((charge) => charge.eventId)
      .filter((eventId): eventId is string => !!eventId),
  );

  const invalidations: Promise<unknown>[] = [
    markQueriesStale(queryClient, { queryKey: ["billing"] }),
    markQueriesStale(queryClient, {
      queryKey: chargeQueryKeys.table(patientId),
    }),
    markQueriesStale(queryClient, {
      queryKey: todaysItemsQueryKeys.patient(patientId),
    }),
    markQueriesStale(queryClient, {
      queryKey: patientQueryKeys.detail(patientId),
    }),
    markQueriesStale(queryClient, {
      queryKey: patientQueryKeys.sidebarCountsForPatient(patientId),
    }),
    markQueriesStale(queryClient, { queryKey: patientQueryKeys.searches() }),
    markQueriesStale(queryClient, {
      queryKey: [...patientQueryKeys.all, "table"],
    }),
    markQueriesStale(queryClient, {
      queryKey: [...patientQueryKeys.all, "ids"],
    }),
    markQueriesStale(queryClient, {
      queryKey: [...patientQueryKeys.all, "list"],
    }),
    markQueriesStale(queryClient, {
      predicate: (query) =>
        startsWith(query.queryKey, patientTimelineQueryKeys.all) &&
        query.queryKey.includes(patientId),
    }),
    markQueriesStale(queryClient, {
      queryKey: ["available-credit", patientId],
    }),
    markQueriesStale(queryClient, {
      queryKey: loyaltyPointQueryKeys.patient(patientId),
    }),
    markQueriesStale(queryClient, {
      queryKey: patientDiscountsQueryKey(patientId),
    }),
    // Checking out converts a draft cart and can bank an overpayment as credit;
    // neither key sits under the ["billing"] prefix above.
    markQueriesStale(queryClient, {
      queryKey: ["patient-drafts", patientId],
    }),
    markQueriesStale(queryClient, {
      queryKey: ["patient-credits", patientId],
    }),
  ];

  if (affectsPackages) {
    invalidations.push(
      markQueriesStale(queryClient, {
        queryKey: patientSoldPackageQueryKeys.all(patientId),
      }),
    );
  }

  if (affectsMemberships) {
    invalidations.push(
      markQueriesStale(queryClient, { queryKey: memberQueryKeys.all }),
      markQueriesStale(queryClient, { queryKey: membershipQueryKeys.all }),
    );
  }

  if (affectsBankedItems) {
    invalidations.push(
      markQueriesStale(queryClient, {
        queryKey: ["patient-banked-items", patientId],
      }),
    );
  }

  if (affectsInventory) {
    invalidations.push(invalidateDoseInventory(queryClient, patientId));
  }

  if (eventIds.size > 0) {
    invalidations.push(
      markQueriesStale(queryClient, { queryKey: eventQueryKeys.lists() }),
      markQueriesStale(queryClient, {
        queryKey: eventQueryKeys.byLocations(),
      }),
      markQueriesStale(queryClient, {
        queryKey: eventQueryKeys.allPatientResults(),
        predicate: (query) => query.queryKey.includes(patientId),
      }),
    );
    eventIds.forEach((eventId) => {
      invalidations.push(
        markQueriesStale(queryClient, {
          queryKey: eventQueryKeys.detail(eventId),
        }),
        markQueriesStale(queryClient, {
          queryKey: eventQueryKeys.tiny(eventId),
        }),
      );
    });
  }

  await Promise.all(invalidations);
}
